import React from 'react';

type FloatLabelProps = React.LabelHTMLAttributes<HTMLLabelElement> & {
  floatLabel: boolean;
  submitted?: boolean;
};

/**
 * Tailwind-only floating label that mirrors the previous EmotionCSS visuals.
 * - Absolute positioned label that floats above the input when `floatLabel` is true
 * - Breakpoint-specific paddings/font-sizes match prior styles as closely as possible
 */
export function FloatLabel({
  floatLabel,
  submitted,
  className,
  children,
  ...rest
}: FloatLabelProps) {
  const base = [
    'absolute',
    submitted ? 'left-auto' : 'left-0',
    'font-medium',
    'text-black',
    'rounded-[16px]',
    'transition-all',
    'duration-300',
    'cursor-text'
  ];

  const floated = [
    'top-0',
    'translate-y-0',
    // base (<320px)
    'py-[2px]',
    'px-0',
    'text-[12px]',
    // global float offsets
    'mt-2',
    'ml-3',
    'bg-white',
    // >=320px
    'small:py-[2px]',
    'small:px-1',
    'small:text-[12px]',
    // >=720px (tablet)
    'tablet:text-[12px]',
    // >=1080px (desktop)
    'desktop:text-[0.8em]',
    'desktop:py-0',
    'desktop:px-1'
  ];

  const notFloated = [
    // vertically center over 56px input height
    'top-1/2',
    '-translate-y-1/2',
    // base (<320px)
    'px-4',
    'text-[14px]',
    // >=320px
    'small:px-4',
    'small:text-[14px]',
    // >=720px (tablet)
    'tablet:text-[14px]',
    // >=1080px (desktop)
    'desktop:text-[16px]',
    'desktop:pl-4',
    'desktop:pr-0'
  ];

  const classes = [
    ...base,
    ...(floatLabel ? floated : notFloated),
    className || ''
  ].join(' ');

  return (
    <label className={classes} {...rest}>
      {children}
    </label>
  );
}
